
import { DashboardAPI } from "@/services/api/Dashboard.service";
import {
  defineComponent,
  ref,
  onMounted,
  inject,
  onUnmounted,
  computed,
  watch,
} from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { TranslationService } from "@/core/services/TranslationService";
import LineChart from "@/components/line-chart/LineChart.vue";
import { dashboardPieChartOptions } from "@/views/form/components/formDashboardConfig";
import { Emitter } from "mitt";
import store from "@/store";
import moment from "moment-timezone";
import { GET_SELECTED_SITES_STR, SITE_STORE } from "@/store/userSite/types";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { FormatMethods } from "@/helpers/data-format";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";

import {
  formTableFilters,
  formTableHeaders,
} from "@/views/form/components/formDashboardConfig";
import { IDashboardFormsDetail } from "@/interfaces/iDashboard";
import { IForm } from "@/interfaces/iForm";
import { FormApi } from "@/services/api/Form.service";
import FormDetailModal from "@/views/form/modal/FormDetailModal.vue";
import LineChartModal from "@/components/modals/chart/LineChartModal.vue";
import ViewFormDetailModal from "@/views/form/modal/ViewFormDetailModal.vue";

type DateRangeEmitter = {
  VZDateRange: string[];
};
type refreshDashboard = {
  refreshDashboard: boolean;
};
export default defineComponent({
  name: "form-dashboard",
  components: {
    LineChart,
    Datatable,
    FormDetailModal,
    LineChartModal,
    ViewFormDetailModal,
  },
  setup() {
    const column = ref(false);
    const color = ref<string>("success");
    const pieChartLoading = ref(false);
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const pieSeries = ref<Array<any>>([]);
    const emitter: Emitter<DateRangeEmitter> | undefined = inject("emitter");
    const emit: Emitter<refreshDashboard> | undefined = inject("emitter");
    const dateFilterValue = ref({
      startDate: moment(moment().subtract(6, "days"), "YYYY-MM-DD HH:mm:ss")
        .tz("UTC")
        .startOf("day")
        .format(),
      endDate: moment(moment(), "YYYY-MM-DDTHH:mm:ssZ").endOf("day").format(),
    });
    const pieChartOptions = ref(dashboardPieChartOptions());
    const onTimeCount = ref<any>();
    const dateOnTimeCount = ref<any>();
    const expiredCount = ref<any>();
    const dateExpiredCount = ref<any>();
    const outsidePeriodCount = ref<any>();
    const dateOutsidePeriodCount = ref<any>();
    const outsidePercentage = ref<any>();
    const expiredPercentage = ref<any>();
    const autorefresh = ref<boolean>(false);
    const ontimePercentage = ref<any>();
    const btnStyles = ref<string>("");

    const { page, total } = usePagination();
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const showDetailDialog = ref<any>(false);
    const showViewDetailDialog = ref<any>(false);
    const showChartDialog = ref<any>(false);
    const defaultFilter = ref({ status: "ACTIVE" });
    const showSearch = true;
    const tableData = ref<IDashboardFormsDetail[]>([]);
    const tableHeader = ref(formTableHeaders());
    const tableFilter = ref(formTableFilters());
    const localPreference = ref({});
    const selectedForm = ref<Partial<IForm>>({});
    const viewSelectedForm = ref({});
    const format = computed(() => {
      return FormatMethods;
    });
    const tableRowClick = (item) => {
      // TODO: For actions
    };
    const openModal = async (modalType, form) => {
      selectedForm.value = {};
      const formObj = await FormApi.getInstance().getFormById(form.webId);
      selectedForm.value = formObj;
      if (modalType == "chart") {
        showChartDialog.value = true;
      } else {
        showDetailDialog.value = true;
      }
    };
    const openViewModal = async (form) => {
      viewSelectedForm.value = {};
      const formObj = await FormApi.getInstance().getFormById(form.webId);
      viewSelectedForm.value = formObj;
      showViewDetailDialog.value = true;
    };
    const queryUpdate = async (params?: Record<string, number | string>) => {
      try {
        loading.value = true;
        localPreference.value = { ...params };

        const parsedDate = moment(dateFilterValue.value?.endDate);
        const startOfDay = parsedDate.startOf("day");
        const endOfDay = startOfDay
          .clone()
          .set({ hour: 23, minute: 59, second: 59 })
          .format("YYYY-MM-DDTHH:mm:ss[Z]");

        const data = await DashboardAPI.getInstance().getFormsDetail({
          ...params,
          startDate: moment(dateFilterValue.value?.startDate)
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm:ssZ"),
          endDate: endOfDay,
        });
        tableData.value = [...data.content];
        total.value = data.totalElements;
        rowsPerPage.value = data.size;
        page.value = ++data.number;
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };
    const refreshList = () => {
      queryUpdate({ ...localPreference.value });
    };
    watch(
      dateFilterValue,
      () => {
        queryUpdate({
          ...defaultFilter.value,
          page: page.value - 1,
          size: rowsPerPage.value,
        });
      },
      { deep: true }
    );

    // Methods
    const getAllDatesCount = async () => {
      try {
        pieChartLoading.value = true;
        // store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        const [onTime, outSide, expired] = await Promise.all([
          DashboardAPI.getInstance().getFormOnTimeData(
            moment(dateFilterValue.value.startDate)
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
            moment(dateFilterValue.value.endDate)
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ")
          ),
          DashboardAPI.getInstance().getFormOutsidePeriodData(
            moment(dateFilterValue.value.startDate)
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
            moment(dateFilterValue.value.endDate)
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ")
          ),
          DashboardAPI.getInstance().getFormExpiredData(
            moment(dateFilterValue.value.startDate)
              .startOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ"),
            moment(dateFilterValue.value.endDate)
              .endOf("day")
              .format("YYYY-MM-DDTHH:mm:ssZ")
          ),
        ]);
        dateOnTimeCount.value = onTime;
        onTimeCount.value = onTime;
        pieSeries.value[0] = dateOnTimeCount.value;
        dateOutsidePeriodCount.value = outSide;
        outsidePeriodCount.value = outSide;
        pieSeries.value[1] = dateOutsidePeriodCount.value;
        dateExpiredCount.value = expired;
        expiredCount.value = expired;
        pieSeries.value[2] = dateExpiredCount.value;

        if (
          onTimeCount.value == 0 &&
          outsidePeriodCount.value == 0 &&
          expiredCount.value == 0
        ) {
          ontimePercentage.value = 0 + "%";
          expiredPercentage.value = 0 + "%";
          outsidePercentage.value = 0 + "%";
        } else {
          let total =
            onTimeCount.value + outsidePeriodCount.value + expiredCount.value;
          outsidePercentage.value = (outsidePeriodCount.value / total) * 100;
          ontimePercentage.value = (onTimeCount.value / total) * 100;
          expiredPercentage.value = (expiredCount.value / total) * 100;
          outsidePercentage.value = Math.round(outsidePercentage.value);
          ontimePercentage.value = Math.round(ontimePercentage.value);
          expiredPercentage.value = Math.round(expiredPercentage.value);
          ontimePercentage.value = ontimePercentage.value + "%";
          expiredPercentage.value = expiredPercentage.value + "%";
          outsidePercentage.value = outsidePercentage.value + "%";
        }
        window.dispatchEvent(new Event("resize"));
      } catch (error) {
        console.error(error);
        //
      } finally {
        pieChartLoading.value = false;
        // store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };
    onMounted(() => {
      setCurrentPageBreadcrumbs(t("common.formsDashboard"), [
        t("common.home"),
        t("common.formsDashboard"),
      ]);
      loading.value = true;
      loadData();
      const intervalId = setInterval(loadData, 900000); // 900000 ms = 15 minutes
      onUnmounted(() => {
        clearInterval(intervalId);
      });
    });
    const loadData = () => {
      getAllDatesCount();
      emitter?.on("VZDateRange", (value) => {
        dateFilterValue.value.startDate = value[0];
        dateFilterValue.value.endDate = value[1];
        dateOnTimeCount.value = 0;
        dateOutsidePeriodCount.value = 0;
        dateExpiredCount.value = 0;
        pieSeries.value[0] = 0;
        pieSeries.value[1] = 0;
        pieSeries.value[2] = 0;
        onTimeCount.value = 0;
        outsidePeriodCount.value = 0;
        expiredCount.value = 0;
        outsidePercentage.value = 0;
        ontimePercentage.value = 0;
        expiredPercentage.value = 0;
        getAllDatesCount();
      });
      emit?.on("refreshDashboard", (value) => {
        loading.value = true;
        dateOnTimeCount.value = 0;
        dateOutsidePeriodCount.value = 0;
        dateExpiredCount.value = 0;
        pieSeries.value[0] = 0;
        pieSeries.value[1] = 0;
        pieSeries.value[2] = 0;
        onTimeCount.value = 0;
        outsidePeriodCount.value = 0;
        expiredCount.value = 0;
        outsidePercentage.value = 0;
        ontimePercentage.value = 0;
        expiredPercentage.value = 0;
        getAllDatesCount();
        queryUpdate({ ...localPreference.value });
      });
    };
    onUnmounted(() => {
      emitter?.off("VZDateRange");
      emit?.off("refreshDashboard");
    });
    const selectedSites = computed(() => {
      const key = `${SITE_STORE}/${GET_SELECTED_SITES_STR}`;
      return store.getters[key] as string;
    });

    watch(selectedSites, async () => {
      await loadData();
    });

    return {
      t,
      btnStyles,
      ontimePercentage,
      expiredPercentage,
      outsidePercentage,
      onTimeCount,
      outsidePeriodCount,
      expiredCount,
      pieChartOptions,
      pieSeries,
      dateFilterValue,
      pieChartLoading,
      column,
      format,
      tableData,
      tableHeader,
      tableFilter,
      showSearch,
      page,
      total,
      rowsPerPage,
      defaultFilter,
      loading,
      tableRowClick,
      queryUpdate,
      refreshList,
      openModal,
      selectedForm,
      showDetailDialog,
      showViewDetailDialog,
      showChartDialog,
      viewSelectedForm,
      openViewModal,
    };
  },
});
